import React, { useCallback, useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { Form } from "react-bootstrap";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm, Controller } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { EditCountryIcon, BackFromCustomerDetailIcon } from "../../../assets";
import {
  SCREEN_PATH,
  EMPTY_STRING,
  adminReduxConstants,
} from "../../../constants";
import { ERROR_TOAST } from "../../../constants/toast.constants";
import {
  customToast,
  isEditorEmpty,
  getSelectOptions,
} from "../../../shared/utility";
import MainSpinner from "../../../shared/components/spinner/MainSpinner";
import CustomSelect from "../../../shared/components/select/CustomSelect";
import RoundedButton from "../../../shared/components/buttons/RoundedButton";
import RichTextEditor from "../../../shared/components/editor/RichTextEditor";
import CustomError from "../../../shared/components/custom-error/CustomError";
import FileUploader from "../../../shared/components/file-uploader/FileUploader";
import convertContentToHTML from "../../../shared/utility-functions/ConvertContentToHtml";
import MainContentWrapper from "../../../shared/components/main-content-wrapper/MainContentWrapper";
import { success } from "../../../redux/actions/utilities";
import { adminActions, countryAction } from "../../../redux/actions";

const countryDetailsSchema = Yup.object().shape({
  countryId: Yup.string().required("Country is required."),
  eligibleRetireCountryIds: Yup.array()
    .required()
    .of(Yup.string().required("Countries for investment are required."))
    .min(1, "At least one country must be selected."),
  images: Yup.array().min(1, "Image is required").required("Image is required"),
});

const { GET_COUNTRIES_SETTINGS_DETAILS } = adminReduxConstants;

const CountryDetails = (props) => {
  const {
    allCountries,
    getAllCountries,
    createCountrySettings,
    updateCountrySettings,
    spinnerActions,
    countriesSettingsDetails,
    getCountriesSettingsDetails,
  } = props;
  const dispatch = useDispatch();

  const { id } = useParams();
  const navigate = useNavigate();
  const { state, pathname } = useLocation();
  const isAdding = pathname.includes(SCREEN_PATH.ADMIN_COUNTRIES_ADD);

  const [content, setContent] = useState(EMPTY_STRING);
  const [htmlContent, setHtmlContent] = useState(EMPTY_STRING);
  const [isEditing, setIsEditing] = useState(!!state?.isEditing || isAdding);

  const handleContentChange = (newContent) => {
    setContent(newContent);
  };

  const methods = useForm({
    mode: "onChange",
    resolver: yupResolver(countryDetailsSchema),
  });

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors },
  } = methods;

  const images = watch("images");

  const navigateBack = () => {
    if (!state?.isEditing && isEditing && !isAdding) {
      getCountriesSettingsDetails(id);
      setIsEditing(false);
    } else {
      navigate("/admin/settings/countries", { replace: true });
    }
  };

  const spinner = useCallback(
    (type) => spinnerActions.findIndex((s) => s === type),
    [spinnerActions]
  );

  useEffect(() => {
    if (!isAdding) {
      reset({
        ...countriesSettingsDetails,
        images: countriesSettingsDetails?.image
          ? [{ image: countriesSettingsDetails?.image }]
          : [],
      });
    }

    if (countriesSettingsDetails?.legalRequirements) {
      const html = convertContentToHTML(
        countriesSettingsDetails.legalRequirements
      );
      setHtmlContent(html);
    }
    // eslint-disable-next-line
  }, [countriesSettingsDetails, isEditing]);

  useEffect(() => {
    getAllCountries();
    if (id) {
      getCountriesSettingsDetails(id);
    }

    return () => {
      dispatch(success({}, GET_COUNTRIES_SETTINGS_DETAILS));
    };

    // eslint-disable-next-line
  }, []);

  const onSubmit = (data) => {
    if (isEditorEmpty(content)) {
      customToast("Country notes must not be empty", ERROR_TOAST);
      return;
    }

    let formdata = new FormData();

    formdata.append("countryId", data.countryId);
    formdata.append("isTaxFree", data?.isTaxFree ?? false);
    formdata.append("isVisaFree", data?.isVisaFree ?? false);
    formdata.append("legalRequirements", content);
    [...data.eligibleRetireCountryIds].forEach((id) => {
      formdata.append("eligibleRetireCountryIds", id);
    });
    [...data.images].forEach((img) => {
      formdata.append(img.name, img);
    });

    if (isAdding) {
      createCountrySettings(formdata, navigateBack);
    } else {
      formdata.append("id", data.id);
      updateCountrySettings(formdata, navigateBack);
    }
  };

  const getCountryName = () => {
    return allCountries.find((f) => f.id === countriesSettingsDetails.countryId)
      ?.name;
  };

  return (
    <MainContentWrapper className="position-relative">
      {spinner(GET_COUNTRIES_SETTINGS_DETAILS) > -1 && (
        <MainSpinner className="admin-spinner" />
      )}
      <img
        src={BackFromCustomerDetailIcon}
        className="customer-detail-back-icon pointer"
        onClick={navigateBack}
        alt="Back"
      />
      <div className="admin-page-heading">Countries Information</div>
      <div className="d-flex align-items-center justify-content-between">
        {!isEditing && !isAdding && (
          <>
            <div className="heading-customer-name">{getCountryName()}</div>
            <div
              className="d-flex align-items-center gap-1 pointer pe-0 pe-md-5"
              onClick={() => setIsEditing(!isEditing)}
            >
              <img
                src={EditCountryIcon}
                className="edit-country-info-image"
                alt="Edit"
              />
              <div className="edit-country-info-text">Edit</div>
            </div>
          </>
        )}
      </div>
      <div className="country-form pe-0 pe-md-5">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row mt-3">
              <div className="col-12 col-lg-5 mt-2">
                <div className="label">Country</div>
                <CustomSelect
                  name="countryId"
                  options={getSelectOptions(allCountries, "name", "id")}
                  className="country-form-select z-index-1000"
                  customDropDown={false}
                  isClearable={false}
                  defaultStyle={false}
                  isEditing={isEditing}
                  showError={false}
                  isBorderRadius
                />
              </div>
              <div className="col-lg-2" />
              <div className="col-12 col-lg-5 mt-2 mb-2">
                <div className="label">Nationality</div>
                <div className="not-allowed">
                  <CustomSelect
                    name="countryId"
                    options={getSelectOptions(
                      allCountries,
                      "nationality",
                      "id"
                    )}
                    className="country-form-select"
                    customDropDown={false}
                    isClearable={false}
                    defaultStyle={false}
                    isEditing={isEditing}
                    readOnly
                    renderedValuesClass="redered-values"
                    showError={false}
                    isBorderRadius
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-12 col-lg-5 mt-2">
                <div className="d-flex column-gap-4 row-gap-3 flex-wrap">
                  <Form.Check
                    label="Is tax free"
                    name="isTaxFree"
                    type="checkbox"
                    checked={watch("isTaxFree")}
                    disabled={!isEditing}
                    onChange={(e) => setValue("isTaxFree", e.target.checked)}
                  />
                  <Form.Check
                    label="Is visa free"
                    name="isVisaFree"
                    type="checkbox"
                    checked={watch("isVisaFree")}
                    disabled={!isEditing}
                    onChange={(e) => setValue("isVisaFree", e.target.checked)}
                  />
                </div>
              </div>
              <div className="col-lg-2" />
              <div className="col-12 col-lg-5 mt-2 mb-2 image-uploader">
                <div className="label d-flex align-items-center">
                  {isEditing ? "Upload Image" : "Image"}
                  <span className="ms-2">
                    <CustomError errorMessage={errors.images?.message} />
                  </span>
                </div>
                <Controller
                  name="images"
                  control={control}
                  render={({ field: { onChange } }) => (
                    <FileUploader
                      disabled={!isEditing}
                      displayDisabled={false}
                      files={images || []}
                      onChange={onChange}
                      handleDelete={(index) => {
                        // Remove image from the array
                        const updatedImage = [...images];
                        updatedImage.splice(index, 1);
                        onChange(updatedImage);
                      }}
                      maxFiles={1}
                    />
                  )}
                />
              </div>
            </div>
            <div className="mt-4 mb-2">
              <div className="label">Countries they can Retire in</div>
              <CustomSelect
                name="eligibleRetireCountryIds"
                options={getSelectOptions(allCountries, "name", "id")}
                className="country-form-select z-index-999"
                customDropDown={false}
                isClearable={false}
                isMulti
                defaultStyle={false}
                isEditing={isEditing}
                renderedValuesClass="redered-values rendered-multi-values d-flex flex-wrap column-gap-4 row-gap-2"
                commaSeparatedMultiValue={false}
                showError={false}
                isBorderRadius
              />
            </div>
            <div className="mt-4 mb-2">
              <div className="country-notes-heading">Country Notes</div>
              {isAdding ||
              (isEditing &&
                countriesSettingsDetails?.legalRequirements !== undefined &&
                countriesSettingsDetails?.legalRequirements !== null) ? (
                <RichTextEditor
                  onContentChange={handleContentChange}
                  initialContent={
                    countriesSettingsDetails?.legalRequirements ?? EMPTY_STRING
                  }
                />
              ) : (
                <div
                  dangerouslySetInnerHTML={{ __html: htmlContent }}
                  className="html-content"
                />
              )}
            </div>
            {isEditing && (
              <div className="agent-approval-buttons-container d-flex justify-content-center gap-3 mt-5">
                <RoundedButton type="submit">
                  {isAdding ? "Submit" : "Update"}
                </RoundedButton>
                <RoundedButton
                  type="button"
                  className="reject-button reject-button-outlined"
                  onClick={navigateBack}
                >
                  Cancel
                </RoundedButton>
              </div>
            )}
          </form>
        </FormProvider>
      </div>
    </MainContentWrapper>
  );
};

const mapStateToProps = (state) => ({
  allCountries: state.countryReducer.allCountries,
  countriesSettingsDetails: state.adminReducer.countriesSettingsDetails,
  spinnerActions: state.systemReducer.spinnerActions,
});

const mapDispatchToProps = (dispatch) => ({
  getAllCountries: () => dispatch(countryAction.getAllCountries()),
  createCountrySettings: (data, func) =>
    dispatch(adminActions.createCountrySettings(data, func)),
  updateCountrySettings: (data, func) =>
    dispatch(adminActions.updateCountrySettings(data, func)),
  getCountriesSettingsDetails: (id) =>
    dispatch(adminActions.getCountriesSettingsDetails(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CountryDetails);
